import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useUser } from "../auth/useUser";
import { useSignOut } from "../auth/useSignOut";
import { useSignOutEverywhere } from "../auth/useSignOutEverywhere";
import { useDeleteAccount } from "../auth/useDeleteAccount";
import {
  type APIToken,
  type APITokenCreated,
  useApiTokensList,
  useCreateApiToken,
  useRevokeApiToken,
} from "../auth/useApiTokens";
import { type ThemePref, useThemeStore } from "../auth/useTheme";
import { useNotificationPrefs } from "../hooks/useNotificationPrefs";
import { notificationsNeedPermission } from "../lib/runNotify";
import { Header } from "../components/Header";
import { Modal } from "../components/Modal";

// Settings page (slice 5): account info, linked accounts, personal API
// tokens (list/create/revoke), session controls (sign out this device,
// sign out everywhere), and a Danger Zone with cascade-delete account.
//
// State worth calling out:
//   - createdToken holds the freshly minted secret (`pmt_<hex>`) ONLY in
//     React state. The orchestrator returns it once and forgets; we never
//     stash it in the query cache or localStorage. It's wiped the moment
//     the user acknowledges the reveal panel.
//   - acknowledgedRef-style behaviour is enforced by `dismissible={false}`
//     on the reveal Modal + a beforeunload guard so an accidental refresh
//     warns the user they're about to lose the only copy.
export function Settings() {
  const { data: user } = useUser();
  const signOut = useSignOut();

  // Created-token reveal: holds the one-time secret in local state until
  // the user acknowledges. While `createdToken !== null` we treat the
  // page as "blocked" — refresh / nav-away should warn (beforeunload),
  // and the modal is non-dismissible.
  const [createdToken, setCreatedToken] = useState<APITokenCreated | null>(null);

  useEffect(() => {
    if (!createdToken) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Modern browsers ignore custom strings but require returnValue to
      // be set to trigger the native "Leave site?" prompt.
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [createdToken]);

  if (!user) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        user={user}
        breadcrumb={
          <>
            <span className="text-ink-600">/</span>
            <span className="font-mono text-ink-300">settings</span>
          </>
        }
      />

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-8 py-12">
        {/* Inner container holds the actual settings content at a
            comfortable form width (~2xl). The outer max-w-6xl matches
            the Product page's chrome so the header and the main
            container share a horizontal extent. Content is left-
            aligned within that extent, mirroring the Product page's
            content+rail layout (rather than a centered narrow form
            that drifts away from the chrome edges). */}
        <div className="max-w-2xl">
        <div className="mb-10 animate-fade-in">
          <p className="font-mono text-xs text-ink-400 tracking-widest uppercase mb-2">
            Account
          </p>
          <h1 className="text-display font-medium text-ink-50 tracking-tightest">
            Settings
          </h1>
        </div>

        <Section label="Profile">
          <Field name="display name" value={user.display_name} />
          <Field name="email" value={user.email} mono />
          <Field name="user id" value={user.id} mono />
          <Field name="plan" value={user.plan} />
        </Section>

        <Section label="Usage">
          <UsageBlock left={user.credit_balance} />
        </Section>

        <Section label="Appearance">
          <AppearancePanel />
        </Section>

        <Section label="Notifications">
          <NotificationsPanel />
        </Section>

        <Section label="Linked accounts">
          <div className="space-y-2">
            {user.provider_subjects?.map((p) => (
              <div
                key={p.provider + p.subject_id}
                className="flex items-center justify-between border border-ink-800 rounded-md px-3 py-2 bg-ink-900/40"
              >
                <span className="text-sm capitalize text-ink-100">{p.provider}</span>
                <span className="font-mono text-[11px] text-ink-400">
                  linked {timeAgo(p.linked_at)}
                </span>
              </div>
            ))}
          </div>
        </Section>

        <Section label="API tokens">
          <ApiTokensPanel onCreated={setCreatedToken} />
        </Section>

        <Section label="Session">
          <SessionPanel signOutPending={signOut.isPending} onSignOut={() => signOut.mutate()} />
        </Section>

        <DangerZone />

        <div className="mt-16 pt-6 border-t border-ink-800 text-xs font-mono text-ink-500 flex items-center justify-between">
          {/* 0176 (Open question 4, ruled 2026-10-07): the commit this build was
              stamped with at build time ("dev" for a local build). 0051 slice 7
              keeps this line. */}
          <span data-testid="app-version">Version {import.meta.env.VITE_BUILD_COMMIT}</span>
          <Link to="/" className="text-ink-400 hover:text-ink-200">
            ← back to dashboard
          </Link>
        </div>
        </div>
      </main>

      {/* Reveal modal: non-dismissible. The user MUST click acknowledge
          so we never silently lose the only copy of a freshly-minted
          secret. We zero the secret out of state on acknowledge. */}
      <CreatedTokenModal
        token={createdToken}
        onAcknowledge={() => setCreatedToken(null)}
      />
    </div>
  );
}

// ---------- Appearance ---------------------------------------------------

function AppearancePanel() {
  const pref = useThemeStore((s) => s.pref);
  const effective = useThemeStore((s) => s.effective);
  const setPref = useThemeStore((s) => s.setPref);

  const opts: { value: ThemePref; label: string }[] = [
    { value: "light", label: "Light" },
    { value: "dark", label: "Dark" },
    { value: "system", label: "System" },
  ];

  return (
    <div>
      <div className="inline-flex border border-ink-700 rounded-md overflow-hidden">
        {opts.map((o, i) => {
          const active = pref === o.value;
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => setPref(o.value)}
              className={[
                "px-3.5 py-2 text-sm transition-colors",
                i > 0 ? "border-l border-ink-700" : "",
                active
                  ? "bg-ink-100 text-ink-950"
                  : "bg-ink-900/40 text-ink-200 hover:bg-ink-800/60",
              ].join(" ")}
              aria-pressed={active}
            >
              {o.label}
            </button>
          );
        })}
      </div>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-widest text-ink-400">
        currently {effective}
        {pref === "system" ? " · following OS" : ""}
      </p>
    </div>
  );
}

// ---------- Notifications (0027 P6) --------------------------------------

function NotificationsPanel() {
  const notify = useNotificationPrefs((s) => s.notificationsOnComplete);
  const sound = useNotificationPrefs((s) => s.soundOnComplete);
  const setNotify = useNotificationPrefs((s) => s.setNotificationsOnComplete);
  const setSound = useNotificationPrefs((s) => s.setSoundOnComplete);
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
    typeof Notification === "undefined" ? "unsupported" : Notification.permission,
  );

  const toggleNotify = async (next: boolean) => {
    if (next && permission !== "granted") {
      if (typeof Notification === "undefined") {
        setPermission("unsupported");
        return;
      }
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p !== "granted") {
        setNotify(false);
        return;
      }
    }
    setNotify(next);
  };

  // Re-request permission for an already-on pref (e.g. enabled on another
  // device, or permission reset in this browser). We do NOT flip the pref off
  // on denial here — the intent is still "on"; it just can't fire in this
  // browser, which the hint below explains.
  const enableHere = async () => {
    if (typeof Notification === "undefined") {
      setPermission("unsupported");
      return;
    }
    setPermission(await Notification.requestPermission());
  };

  // The toggle reflects the server-persisted pref, but the OS permission is
  // per-browser. If the pref is on yet permission isn't granted here, the
  // toggle looks enabled but nothing fires — surface that instead of lying.
  const needsPermission = notificationsNeedPermission({ prefEnabled: notify, permission });

  return (
    <div className="space-y-3">
      <ToggleRow
        checked={notify}
        onChange={toggleNotify}
        label="Notify me when a run finishes"
        hint={
          permission === "unsupported"
            ? "This browser doesn't support notifications."
            : "A browser notification when an analysis completes while this tab is in the background."
        }
      />
      {needsPermission && (
        <div className="ml-7 flex items-center gap-3 rounded-md border border-warn-solid/40 bg-warn-solid/10 px-3 py-2 text-xs">
          <span className="text-ink-200">
            {permission === "denied"
              ? "This browser is blocking notifications — allow them in your browser's site settings, then reload."
              : "This browser hasn't granted notification permission yet, so nothing will fire here."}
          </span>
          {permission === "default" && (
            <button
              type="button"
              onClick={enableHere}
              className="ml-auto shrink-0 rounded-md bg-accent px-2.5 py-1 font-medium text-ink-950 hover:bg-accent-glow"
            >
              Enable
            </button>
          )}
        </div>
      )}
      <ToggleRow
        checked={sound}
        onChange={(v) => setSound(v)}
        label="Play a sound when a run finishes"
        hint="A short chime on completion."
      />
    </div>
  );
}

function ToggleRow({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint: string;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer border border-ink-800 rounded-md px-3 py-2.5 bg-ink-900/40">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 accent-accent"
      />
      <span className="flex-1">
        <span className="block text-sm text-ink-100">{label}</span>
        <span className="block text-[11px] text-ink-400 mt-0.5">{hint}</span>
      </span>
    </label>
  );
}

// ---------- API tokens ---------------------------------------------------

function ApiTokensPanel({ onCreated }: { onCreated: (t: APITokenCreated) => void }) {
  const list = useApiTokensList();
  const create = useCreateApiToken();
  const revoke = useRevokeApiToken();

  const [name, setName] = useState("");
  // Token row marked for revoke confirmation. null = no modal open.
  const [revokeTarget, setRevokeTarget] = useState<APIToken | null>(null);

  const tokens = list.data ?? [];

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || create.isPending) return;
    create.mutate(trimmed, {
      onSuccess: (token) => {
        setName("");
        onCreated(token);
      },
    });
  };

  return (
    <div>
      <form onSubmit={submit} className="flex items-center gap-2 mb-4">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Personal CLI access"
          maxLength={120}
          className="flex-1 bg-ink-900/60 border border-ink-700 hover:border-ink-600 focus:border-accent focus:outline-none rounded-md px-3 py-2 text-sm text-ink-100 placeholder:text-ink-500 transition-colors"
        />
        <button
          type="submit"
          disabled={!name.trim() || create.isPending}
          className="rounded-md bg-accent text-ink-950 hover:bg-accent-glow px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {create.isPending ? "minting…" : "Create token"}
        </button>
      </form>

      {create.isError ? (
        <p className="mb-3 text-xs text-danger">
          Couldn't mint token. Try again.
        </p>
      ) : null}

      {list.isLoading ? (
        <p className="text-xs text-ink-400">Loading tokens…</p>
      ) : list.isError ? (
        <p className="text-xs text-danger">Couldn't load tokens.</p>
      ) : tokens.length === 0 ? (
        <p className="text-xs text-ink-400">
          No tokens yet. Create one above for CLI / scripted access.
        </p>
      ) : (
        <ul className="border-t border-ink-800">
          {tokens.map((t) => (
            <TokenRow
              key={t.token_id}
              token={t}
              onRevokeClick={() => setRevokeTarget(t)}
              revoking={revoke.isPending && revokeTarget?.token_id === t.token_id}
            />
          ))}
        </ul>
      )}

      <RevokeModal
        target={revokeTarget}
        pending={revoke.isPending}
        onClose={() => {
          if (!revoke.isPending) setRevokeTarget(null);
        }}
        onConfirm={() => {
          if (!revokeTarget) return;
          revoke.mutate(revokeTarget.token_id, {
            onSuccess: () => setRevokeTarget(null),
          });
        }}
      />
    </div>
  );
}

function TokenRow({
  token,
  onRevokeClick,
  revoking,
}: {
  token: APIToken;
  onRevokeClick: () => void;
  revoking: boolean;
}) {
  return (
    <li className="flex items-center justify-between gap-4 py-3 border-b border-ink-800 last:border-b-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm text-ink-100 truncate">{token.name}</span>
          {token.last_used_ip ? (
            <span className="font-mono text-[10px] uppercase tracking-widest text-ink-300 border border-ink-700 rounded px-1.5 py-0.5">
              ip {token.last_used_ip}
            </span>
          ) : null}
        </div>
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 font-mono text-[11px] text-ink-400">
          <span className="truncate">{token.token_id}</span>
          <span>created {formatDateTime(token.created_at)}</span>
          <span>
            last used{" "}
            {token.last_used_at ? formatDateTime(token.last_used_at) : "never"}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={onRevokeClick}
        disabled={revoking}
        className="rounded-md border border-danger-solid/40 text-danger hover:border-danger-solid/70 hover:text-danger-strong px-3 py-1.5 text-xs transition-colors disabled:opacity-50"
      >
        {revoking ? "revoking…" : "Revoke"}
      </button>
    </li>
  );
}

function RevokeModal({
  target,
  pending,
  onClose,
  onConfirm,
}: {
  target: APIToken | null;
  pending: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal open={target !== null} onClose={onClose} title="Revoke token">
      <p className="text-sm text-ink-100 mb-2">
        Revoke <span className="font-mono text-ink-50">{target?.name}</span>?
      </p>
      <p className="text-xs text-ink-300 mb-5">
        Any client using it will fail authentication immediately. This cannot
        be undone.
      </p>
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={pending}
          className="rounded-md border border-ink-700 hover:border-ink-500 px-3.5 py-2 text-sm text-ink-100 transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={pending}
          className="rounded-md border border-danger-solid/40 bg-danger-solid/10 text-danger-strong hover:border-danger-solid/70 hover:text-ink-50 px-3.5 py-2 text-sm transition-colors disabled:opacity-50"
        >
          {pending ? "revoking…" : "Revoke token"}
        </button>
      </div>
    </Modal>
  );
}

function CreatedTokenModal({
  token,
  onAcknowledge,
}: {
  token: APITokenCreated | null;
  onAcknowledge: () => void;
}) {
  const [copied, setCopied] = useState(false);

  // Reset copy-state when a new token shows up.
  useEffect(() => {
    setCopied(false);
  }, [token?.token_id]);

  const onCopy = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(token.secret);
      setCopied(true);
    } catch {
      // Clipboard API can be blocked in some embedded contexts; surface
      // a fallback by selecting nothing graceful — just leave copied=false.
      setCopied(false);
    }
  };

  return (
    <Modal
      open={token !== null}
      onClose={onAcknowledge}
      dismissible={false}
      title="API token created"
      width="max-w-lg"
    >
      <p className="text-sm text-ink-100 mb-1">
        Save this secret now — it won't be shown again.
      </p>
      <p className="text-xs text-ink-300 mb-4">
        Use it as <code className="font-mono text-ink-200">Authorization: Bearer …</code>.
        If you lose it, revoke this token and create a new one.
      </p>
      <pre className="bg-ink-950 border border-ink-800 rounded-md p-3 text-[12px] font-mono text-accent break-all whitespace-pre-wrap mb-3">
        {token?.secret ?? ""}
      </pre>
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={onCopy}
          className="rounded-md border border-ink-700 hover:border-ink-500 px-3.5 py-2 text-sm text-ink-100 transition-colors"
        >
          {copied ? "Copied" : "Copy to clipboard"}
        </button>
        <button
          type="button"
          onClick={onAcknowledge}
          className="rounded-md bg-accent text-ink-950 hover:bg-accent-glow px-3.5 py-2 text-sm font-medium transition-colors"
        >
          I've saved it — close
        </button>
      </div>
    </Modal>
  );
}

// ---------- Session ------------------------------------------------------

function SessionPanel({
  signOutPending,
  onSignOut,
}: {
  signOutPending: boolean;
  onSignOut: () => void;
}) {
  const everywhere = useSignOutEverywhere();
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={onSignOut}
        disabled={signOutPending}
        className="rounded-md border border-ink-700 hover:border-ink-500 px-3.5 py-2 text-sm text-ink-100 transition-colors disabled:opacity-50"
      >
        {signOutPending ? "signing out…" : "Sign out (this device)"}
      </button>
      <button
        type="button"
        onClick={() => setConfirmOpen(true)}
        disabled={everywhere.isPending}
        className="rounded-md border border-ink-700 hover:border-ink-500 px-3.5 py-2 text-sm text-ink-100 transition-colors disabled:opacity-50"
      >
        {everywhere.isPending ? "signing out…" : "Sign out everywhere"}
      </button>

      <Modal
        open={confirmOpen}
        onClose={() => {
          if (!everywhere.isPending) setConfirmOpen(false);
        }}
        title="Sign out everywhere"
      >
        <p className="text-sm text-ink-100 mb-2">
          This will sign you out on all devices.
        </p>
        <p className="text-xs text-ink-300 mb-5">
          You'll need to sign in again on each. API tokens are not affected —
          revoke them separately if needed.
        </p>
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setConfirmOpen(false)}
            disabled={everywhere.isPending}
            className="rounded-md border border-ink-700 hover:border-ink-500 px-3.5 py-2 text-sm text-ink-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => everywhere.mutate()}
            disabled={everywhere.isPending}
            className="rounded-md bg-accent text-ink-950 hover:bg-accent-glow px-3.5 py-2 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {everywhere.isPending ? "signing out…" : "Sign out everywhere"}
          </button>
        </div>
      </Modal>
    </div>
  );
}

// ---------- Danger zone --------------------------------------------------

function DangerZone() {
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const del = useDeleteAccount();

  const armed = typed === "DELETE";

  const closeIfIdle = () => {
    if (!del.isPending && !del.isSuccess) {
      setOpen(false);
      setTyped("");
    }
  };

  return (
    <section className="mt-12 pt-8 border-t border-ink-800">
      <h2 className="font-mono text-[10px] uppercase tracking-widest text-danger/80 mb-3">
        Danger zone
      </h2>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 border border-danger-solid/30 bg-danger-solid/5 rounded-md px-4 py-3">
        <div>
          <p className="text-sm text-ink-100">Delete my account</p>
          <p className="text-xs text-ink-300 mt-0.5">
            Permanently removes your account, products, runs, drafts, and tokens.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="self-start sm:self-auto rounded-md border border-danger-solid/40 text-danger hover:border-danger-solid/70 hover:text-danger-strong px-3.5 py-2 text-sm transition-colors"
        >
          Delete account
        </button>
      </div>

      <Modal open={open} onClose={closeIfIdle} title="Delete account">
        <p className="text-sm text-ink-100 mb-2">
          This will permanently delete your account, all products, runs,
          drafts, and tokens.
        </p>
        <p className="text-xs text-danger mb-4">
          This action cannot be undone.
        </p>
        <label className="block text-xs text-ink-300 mb-1">
          Type <span className="font-mono text-ink-100">DELETE</span> to confirm
        </label>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          spellCheck={false}
          disabled={del.isPending}
          className="w-full bg-ink-950 border border-ink-700 hover:border-ink-600 focus:border-danger focus:outline-none rounded-md px-3 py-2 text-sm font-mono text-ink-100 placeholder:text-ink-500 transition-colors mb-5"
          placeholder="DELETE"
        />
        {del.isError ? (
          <p className="-mt-3 mb-4 text-xs text-danger">
            Couldn't delete account. Try again or contact support.
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={closeIfIdle}
            disabled={del.isPending}
            className="rounded-md border border-ink-700 hover:border-ink-500 px-3.5 py-2 text-sm text-ink-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => del.mutate()}
            disabled={!armed || del.isPending}
            className="rounded-md border border-danger-solid/40 bg-danger-solid/10 text-danger-strong hover:border-danger-solid/70 hover:text-ink-50 px-3.5 py-2 text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {del.isPending ? "deleting…" : "Delete my account"}
          </button>
        </div>
      </Modal>
    </section>
  );
}

// ---------- Usage (0051 slice 0, FD-10) ----------------------------------

function UsageBlock({ left }: { left: number }) {
  return (
    <div data-testid="usage-block" className="border border-ink-800 rounded-md px-4 py-3 bg-ink-900/40">
      <span data-testid="usage-left" className="text-sm text-ink-100 tabular-nums">{left} left</span>
    </div>
  );
}

// ---------- Layout primitives + helpers ----------------------------------

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="font-mono text-[10px] uppercase tracking-widest text-ink-400 mb-3 pb-2 border-b border-ink-800">
        {label}
      </h2>
      <div>{children}</div>
    </section>
  );
}

function Field({ name, value, mono }: { name: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between py-2 border-b border-ink-800/50 last:border-b-0">
      <span className="font-mono text-[11px] uppercase tracking-widest text-ink-400">
        {name}
      </span>
      <span className={`text-sm text-ink-200 ${mono ? "font-mono text-[12px]" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function timeAgo(iso: string | undefined) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days < 0) return "—";
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

// formatDateTime renders ISO timestamps as a compact mono "YYYY-MM-DD HH:MM"
// in the user's local TZ. Mono + fixed width keeps token-list rows from
// jiggling on hover/refetch.
function formatDateTime(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

